import { useLocation } from "react-router-dom";
import { useAppShell, useKeyboardOpen } from "@/lib/useAppShell";
import { useUI } from "@/stores/ui";

/**
 * Screens that take the whole height or bring their own bottom bar: the tab bar isn't rendered there.
 * (Chat rooms, checkout and payment, the product page's buy bar, the post-an-ad / onboarding / ad wizard
 * forms. Auth screens and billing use their own layouts without the bar.)
 */
const NO_TAB_BAR = [
  /^\/chats\/[^/]+/,
  /^\/wallet\/topup/,
  /^\/messages\/[^/]+/,
  /^\/p\//,
  /^\/checkout/,
  /^\/payment\//,
  /^\/billing/,
  /^\/vendor\/onboarding/,
  /^\/vendor\/listings\/(new|[^/]+)$/,
  /^\/advertise\/new/,
  /^\/ads\/[^/]+\/edit/,
  /^\/(login|register|forgot-password|reset-password|verify)/,
];

/** Browse screens where the bar slides away while scrolling down (and back on the way up). */
const AUTO_HIDE = [/^\/$/, /^\/c\//, /^\/search/, /^\/store\//, /^\/deals/];

export function hidesTabBar(pathname: string): boolean {
  return NO_TAB_BAR.some((r) => r.test(pathname));
}

export function autoHidesTabBar(pathname: string): boolean {
  return AUTO_HIDE.some((r) => r.test(pathname));
}

/** The bar is rendered unless the screen opts out, a full-screen viewer is open, or the keyboard is up. */
export function useTabBarVisible(): boolean {
  const { pathname } = useLocation();
  const { isMobileShell } = useAppShell();
  const keyboard = useKeyboardOpen();
  const overlay = useUI((s) => s.fullscreenOpen);
  return isMobileShell && !hidesTabBar(pathname) && !keyboard && !overlay;
}
